import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import type { RecordConfusionInput } from './sentimentTypes';

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

interface KuduIdentity {
  profile?: RecordConfusionInput['userProfile'];
  userId: string;
}

export function buildConfusionSentimentRequest(
  input: RecordConfusionInput,
  identity: KuduIdentity,
) {
  const componentUid = input.component.uid ?? input.component.id ?? input.componentId;

  return {
    component: {
      content_unit_annotations:
        input.component.content_unit_annotations ??
        input.component.contentUnitAnnotations ??
        [],
      section_stats: input.component.section_stats ?? input.component.sectionStats ?? [],
      uid: String(componentUid),
    },
    component_id: input.componentId,
    course_id: input.courseId,
    user: {
      id: identity.userId,
      profile: identity.profile ?? null,
    },
    user_id: identity.userId,
    video_section_update: {
      sentiment_time: Math.max(0, Math.round(input.sentimentTime)),
      video_id: input.videoId,
      video_section_id: input.videoSectionId ?? '',
    },
  };
}

export function useConfusedSentiment() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getIdToken, user } = useAuth();

  return useMutation<void, ApiError, RecordConfusionInput>({
    mutationFn: async (input) => {
      const { userProfile, videoSectionId } = input;
      if (!videoSectionId) {
        return;
      }

      if (!user) {
        throw createDataError('You must be signed in to record confusion feedback.');
      }

      const userId = getKuduUserId(user) ?? user.uid;

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      await client.invokeKuduMethod<
        unknown | LegacyKuduResponseEnvelope<unknown>,
        ReturnType<typeof buildConfusionSentimentRequest>
      >(
        'AddOrUpdateCourseStudentComponentV2',
        buildConfusionSentimentRequest(input, {
          profile: userProfile,
          userId,
        }),
      );
    },
  });
}
